import { useCallback, useEffect, useState } from "react";

import { useGetSessionsByPlayerIdQuery } from "../../../services";
import { sessionListLimit } from "../../../shared/Constants";
import { Session } from "../../../types";

export interface UsePlayerSessionsOptions {
  /** For auto mode: pass playerId and fetch starts when it's truthy. Omit for manual mode. */
  playerId?: string | null;
}

export interface UsePlayerSessionsResult {
  sessions: Session[];
  isFetching: boolean;
  isFetchingAll: boolean;
  error: string | null;
  allowRetry: boolean;
  /** Player ID we're fetching for (for matching results to selected player) */
  activePlayerId: string;
  startFetch: (playerId: string) => void;
  reset: () => void;
}

function getErrorStatus(err: unknown): number | null {
  if (err && typeof err === "object" && "status" in err) {
    return (err as { status: number }).status;
  }
  if (
    err &&
    typeof err === "object" &&
    "data" in err &&
    (err.data as { statusCode?: number })?.statusCode
  ) {
    return (err.data as { statusCode?: number }).statusCode ?? null;
  }
  return null;
}

/**
 * Fetches and paginates player sessions via useGetSessionsByPlayerIdQuery.
 * Supports auto mode (pass playerId, fetch starts when truthy) and manual mode
 * (call startFetch when ready).
 *
 * Used by DefinePlayerDataSet (auto) and ChatSetup (manual).
 */
export function usePlayerSessions(
  options: UsePlayerSessionsOptions = {},
): UsePlayerSessionsResult {
  const { playerId: externalPlayerId } = options;

  const [playerIdForQuery, setPlayerIdForQuery] = useState("");
  const [shouldFetch, setShouldFetch] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowRetry, setAllowRetry] = useState(true);

  const reset = useCallback(() => {
    setPlayerIdForQuery("");
    setShouldFetch(false);
    setIsFetchingAll(false);
    setSessions([]);
    setOffset(0);
    setError(null);
    setAllowRetry(true);
  }, []);

  const startFetch = useCallback((playerId: string) => {
    setPlayerIdForQuery(playerId);
    setShouldFetch(true);
    setIsFetchingAll(true);
    setSessions([]);
    setOffset(0);
    setError(null);
    setAllowRetry(true);
  }, []);

  // Auto mode: sync when externalPlayerId is provided
  useEffect(() => {
    if (externalPlayerId === undefined) return; // manual mode

    if (!externalPlayerId) {
      reset();
      return;
    }
    if (externalPlayerId !== playerIdForQuery) {
      setPlayerIdForQuery(externalPlayerId);
      setShouldFetch(true);
      setIsFetchingAll(true);
      setSessions([]);
      setOffset(0);
      setError(null);
    }
  }, [externalPlayerId, playerIdForQuery, reset]);

  const querySkip = !shouldFetch || !playerIdForQuery;

  const {
    data: querySessions,
    originalArgs: sessionsOriginalArgs,
    isError: isSessionsError,
    isFetching: isSessionsFetching,
    error: sessionsError,
  } = useGetSessionsByPlayerIdQuery(
    {
      orgPlayerIds: playerIdForQuery ? [playerIdForQuery] : [],
      limit: sessionListLimit,
      offset,
      status: "processed",
    },
    { skip: querySkip },
  );

  // Pagination: accumulate sessions and advance offset
  useEffect(() => {
    if (!querySessions || !shouldFetch || !playerIdForQuery) return;
    if (isSessionsFetching) return;
    if (
      sessionsOriginalArgs &&
      sessionsOriginalArgs.orgPlayerIds?.[0] !== playerIdForQuery
    ) {
      return;
    }

    const currentOffset = sessionsOriginalArgs?.offset ?? offset;

    if (querySessions.length === sessionListLimit) {
      setOffset(currentOffset + sessionListLimit);
      setSessions((prev) =>
        currentOffset === 0 ? querySessions : prev.concat(querySessions),
      );
      if (currentOffset === 0) setError(null);
    } else {
      setSessions((prev) =>
        currentOffset === 0 ? querySessions : prev.concat(querySessions),
      );
      setShouldFetch(false);
      setIsFetchingAll(false);
      setError(null);
    }
  }, [
    querySessions,
    sessionsOriginalArgs,
    shouldFetch,
    playerIdForQuery,
    offset,
    isSessionsFetching,
  ]);

  // Error handling - only set error when failed query matches current player (avoids stale error when user switches)
  useEffect(() => {
    if (!isSessionsError || !shouldFetch || !playerIdForQuery) return;
    if (
      externalPlayerId !== undefined &&
      playerIdForQuery !== externalPlayerId
    ) {
      return;
    }

    setShouldFetch(false);
    setIsFetchingAll(false);
    setSessions([]);
    setOffset(0);

    const status = getErrorStatus(sessionsError);

    if (status === 404) {
      setError("No sessions found for this player.");
      setAllowRetry(false);
    } else {
      setError("Failed to load sessions for this player. Please try again.");
      setAllowRetry(true);
    }
  }, [
    isSessionsError,
    shouldFetch,
    playerIdForQuery,
    externalPlayerId,
    sessionsError,
  ]);

  return {
    sessions,
    isFetching: isSessionsFetching,
    isFetchingAll,
    error,
    allowRetry,
    activePlayerId: playerIdForQuery,
    startFetch,
    reset,
  };
}
